/**
 * Fiction pipeline phase list and content-type set.
 *
 * Used by `apps/web` (React SPA) directly.
 *
 * The Directus extension `extensions/directus-extension-document-workspace`
 * has its own copy of these constants inlined in `src/DocumentWorkspace.vue`
 * because the extension's build sandbox can't reach this path. Keep them in
 * sync — when you change phase labels or add a fiction content_type here,
 * mirror the change there.
 *
 * Adding a new fiction content_type? Update `FICTION_CONTENT_TYPES` and the
 * `content_type` choice list in `bin/setup-directus.ts`. Adding a new phase?
 * Update `FICTION_PIPELINE_PHASES`, the `generation_phases` enum in
 * `bin/setup-directus.ts`, and the `GenerationPhase` union in `ui-state.ts`.
 */

export interface PipelinePhase {
  value: string;
  label: string;
  icon: string;
}

export const MAIN_PIPELINE_PHASES: readonly PipelinePhase[] = [
  { value: "phase_1_researched", label: "Scout", icon: "search" },
  { value: "phase_2_outlined", label: "Librarian", icon: "menu_book" },
  { value: "phase_3_draft_generated", label: "Writer", icon: "edit" },
  { value: "phase_4_fact_checked", label: "Auditor", icon: "fact_check" },
  { value: "phase_5_corrected", label: "Editor", icon: "edit_note" },
  // FEAT-084 — the post-Editor voice check. Shared with the SPA, so adding it
  // here adds a visible stage to the pipeline display, which is intended.
  { value: "phase_5b_voice_scored", label: "Voice Check", icon: "record_voice_over" },
  { value: "phase_6_seo_optimized", label: "SEO Optimizer", icon: "search" },
  { value: "phase_7_art_directed", label: "Art Director", icon: "image" },
  { value: "phase_8_published", label: "Publisher", icon: "publish" },
];

export const FICTION_PIPELINE_PHASES: readonly PipelinePhase[] = [
  { value: "fiction_phase_1_worldbuilt", label: "Worldbuilder", icon: "public" },
  { value: "fiction_phase_2_architected", label: "Architect", icon: "account_tree" },
  { value: "fiction_phase_3_draft_generated", label: "Storyteller", icon: "auto_stories" },
  { value: "fiction_phase_4_continuity_checked", label: "Continuity", icon: "fact_check" },
  { value: "fiction_phase_5_corrected", label: "Prose Editor", icon: "edit_note" },
  { value: "fiction_phase_6_illustrated", label: "Illustrator", icon: "palette" },
  { value: "fiction_phase_7_published", label: "Publisher", icon: "publish" },
];

/**
 * FEAT-025 — the seven content-pipeline stages that can be gated for human
 * review, with UI labels. Excludes phase_8_published: the publisher approval
 * gate is always enforced and never user-configurable. The API keeps a parallel
 * value-only list in apps/api/src/lib/review-stages.ts — keep them in sync.
 */
export interface ReviewStage {
  value: string;
  label: string;
}

export const REVIEW_STAGES: readonly ReviewStage[] = [
  { value: "phase_1_researched", label: "Research" },
  { value: "phase_2_outlined", label: "Library & Outline" },
  { value: "phase_3_draft_generated", label: "Draft" },
  { value: "phase_4_fact_checked", label: "Fact-check" },
  { value: "phase_5_corrected", label: "Edit" },
  { value: "phase_6_seo_optimized", label: "SEO / AEO" },
  { value: "phase_7_art_directed", label: "Imagery" },
];

export const FICTION_CONTENT_TYPES: ReadonlySet<string> = new Set([
  "short_story",
  "serial_episode",
  "novella_chapter",
  "flash_fiction",
]);

/**
 * Pick the right phase list for an article. Trust `generation_phases` first
 * (`fiction_*` is unambiguous); fall back to `content_type` so brand-new
 * fiction articles with no phase yet still show the fiction timeline.
 */
export function isFictionArticle(input: {
  content_type?: string | null;
  generation_phases?: string | null;
}): boolean {
  if (input.generation_phases?.startsWith("fiction_")) return true;
  return FICTION_CONTENT_TYPES.has(input.content_type ?? "");
}

export function pipelinePhasesFor(input: {
  content_type?: string | null;
  generation_phases?: string | null;
}): readonly PipelinePhase[] {
  return isFictionArticle(input) ? FICTION_PIPELINE_PHASES : MAIN_PIPELINE_PHASES;
}
